import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MicIcon from '@mui/icons-material/Mic';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import { useThemeContext } from '../../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Style factory — mirrors Summary.js pattern: (themeMode, isMobile) => styles
// ---------------------------------------------------------------------------
const createStyles = (themeMode, isMobile) => {
  const isLight = themeMode === 'light';

  return {
    // Outer wrapper — glassmorphism card matching Summary.js glassCard
    wrapper: {
      position: 'relative',
      background: isLight
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(255, 255, 255, 0.06)',
      backdropFilter: 'blur(20px) saturate(180%)',
      border: isLight
        ? '1px solid rgba(255, 255, 255, 0.9)'
        : '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '14px 12px' : '18px 20px',
      boxShadow: isLight
        ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.04)'
        : '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    },

    // Title row
    title: {
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: isLight ? 'rgba(15, 23, 42, 0.55)' : 'rgba(248, 250, 252, 0.5)',
      marginBottom: isMobile ? '10px' : '12px',
    },

    // Scroll container
    scrollRow: {
      display: 'flex',
      gap: '16px',
      overflowX: 'auto',
      scrollbarWidth: 'none', // Firefox
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      paddingBottom: '4px',
      // Hide scrollbar across browsers
      '&::-webkit-scrollbar': { display: 'none' },
      // Desktop: centre items when they fit
      ...(!isMobile && {
        justifyContent: 'center',
      }),
    },

    // Individual action column
    actionItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      scrollSnapAlign: 'center',
      flexShrink: 0,
      minWidth: isMobile ? '60px' : '68px',
    },

    // Circle button
    circleButton: {
      width: 56,
      height: 56,
      borderRadius: '50%',
      transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease',
      boxShadow: isLight
        ? '0 2px 8px rgba(0,0,0,0.10)'
        : '0 2px 10px rgba(0,0,0,0.35)',
      '&:hover': {
        transform: 'scale(1.05)',
      },
      '&:active': {
        transform: 'scale(0.97)',
      },
    },

    // Label below circle
    label: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.2,
      textAlign: 'center',
      color: isLight ? '#334155' : '#cbd5e1',
      maxWidth: '72px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
};

// ---------------------------------------------------------------------------
// Gradient palette — each action gets a unique soft gradient
// ---------------------------------------------------------------------------
const GRADIENTS = {
  speak:    'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
  add:      'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',   // blue
  scan:     'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',   // purple
  budget:   'linear-gradient(135deg, #10b981 0%, #34d399 100%)',   // green
  split:    'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',   // orange
  insights: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',   // cyan
  settings: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',   // grey
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const QuickActions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { themeMode } = useThemeContext();

  // Responsive — same approach as Summary.js (window.innerWidth + resize)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const isMobile = windowWidth < 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = useMemo(
    () => createStyles(themeMode, isMobile),
    [themeMode, isMobile]
  );

  // Action definitions — memoised to avoid re-creating on every render
  const actions = useMemo(
    () => [
      {
        key: 'speak',
        icon: <MicIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.speak', 'Speak'),
        gradient: GRADIENTS.speak,
        onClick: () => window.dispatchEvent(new Event('sk:voice-start')),
      },
      {
        key: 'add',
        icon: <AddIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.addTransaction', 'Add'),
        gradient: GRADIENTS.add,
        route: '/dashboard/transactions',
      },
      {
        key: 'scan',
        icon: <CameraAltIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.scanReceipt', 'Scan'),
        gradient: GRADIENTS.scan,
        route: '/dashboard/receipts',
      },
      {
        key: 'budget',
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.setBudget', 'Budget'),
        gradient: GRADIENTS.budget,
        route: '/dashboard/budget',
      },
      {
        key: 'split',
        icon: <CallSplitIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.splitBill', 'Split'),
        gradient: GRADIENTS.split,
        route: '/dashboard/split-bills',
      },
      {
        key: 'insights',
        icon: <InsightsIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.aiInsights', 'Insights'),
        gradient: GRADIENTS.insights,
        route: '/dashboard/insights',
      },
      {
        key: 'settings',
        icon: <SettingsIcon sx={{ fontSize: 24, color: '#fff' }} />,
        label: t('dashboard.quickActions.settings', 'Settings'),
        gradient: GRADIENTS.settings,
        route: '/settings',
      },
    ],
    [t]
  );

  const handleNavigate = useCallback(
    (route) => () => navigate(route),
    [navigate]
  );

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.title}>
        {t('dashboard.quickActions.title', 'Quick Actions')}
      </Typography>

      <Box sx={styles.scrollRow}>
        {actions.map((action) => (
          <Box key={action.key} sx={styles.actionItem}>
            <IconButton
              onClick={action.onClick || handleNavigate(action.route)}
              aria-label={action.label}
              sx={{
                ...styles.circleButton,
                background: action.gradient,
              }}
            >
              {action.icon}
            </IconButton>
            <Typography sx={styles.label}>{action.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QuickActions;
